import {
  CAPABILITY_LIMITS,
  capabilityNearestKey,
  capabilityWordsKey,
  groupRefusalEntries,
  partsListedBesideCard,
  SUPPORTED_RATING_UNIT_IDS,
  type CapabilityId,
  type QuestionnaireChatNotDone,
  type QuestionnaireNotDonePart,
} from "@digital-twins/shared-types";
import type { TFunction } from "i18next";

import { chatT } from "@/i18n";
import { ratingScaleUserName, ruleLine } from "./questionnaireIssueCopy";
import { languageName } from "./turnCommandCopy";

/**
 * The builder's words for the questions an objective-panel edit kept although
 * they served only a learning objective it removed (F24): the model only
 * listed them; the line says they stay and where to remove them, in the
 * interface's language, with each question's title a list item under it.
 */
export function removedObjectiveQuestionLines(
  titles: ReadonlyArray<string>,
  t: TFunction<"app">,
): Array<string> {
  return titles.length > 0
    ? [
        t("questionnaire.removedObjectiveQuestions", { count: titles.length }),
        ...titles.map((title) => `- ${title}`),
      ]
    : [];
}

/**
 * Why the builder cannot do a part as asked, from the capability it needs
 * (S4.11), with the limit its registry owner states, in the design the turn
 * committed under when the capability's words differ by design
 * (`capabilityWordsKey`).
 */
function capabilityWords(
  capability: CapabilityId,
  exposure: QuestionnaireChatNotDone["exposure"],
  t: TFunction<"app">,
  lng: string | undefined,
): string {
  // The key is built from the registry's design keys, not the typed key union.
  const words = t as unknown as (key: string, options: Record<string, unknown>) => string;
  return words(`questionnaire.capability.${capabilityWordsKey(capability, exposure)}`, {
    scales: SUPPORTED_RATING_UNIT_IDS.map((unit) =>
      ratingScaleUserName(unit, lng),
    ).join(", "),
    max: CAPABILITY_LIMITS.matrixMaxColumns,
    ceiling: CAPABILITY_LIMITS.researchQuestionCeiling,
    competitors: CAPABILITY_LIMITS.brandCompetitorMax,
    languages: CAPABILITY_LIMITS.surveyLanguages
      .map((code) => languageName(code, lng))
      .join(", "),
  });
}

/**
 * The builder's words for what a turn did not do (owner decision F16), in the
 * interface's language. The model only named each part; the reviewed line
 * says why, follows what the user acted in (the chat or the objective panel),
 * and names the study's asset panel by the heading that panel shows. Each part
 * is a list item under its line, so the part's own punctuation never matters.
 * The list is the turn's refusals (gate 2026-10-15, L02): a part the
 * questionnaire already does is no refusal, and the reply says what it
 * already does. A part done in its nearest supported version (S4.11) comes
 * first, under a line saying it was not possible as asked. When its capability declares what the
 * nearest version does (C06-1, the registry's typed `nearest`), the part says
 * that, never the capability's gloss of what is not offered; a limit's
 * nearest part keeps the limit as its reason. A part not done that needs a
 * capability the builder lacks says which; one a code rule's refusal left
 * undone says that rule's catalog line (L4a). The parts listed beside the card
 * the reply carries (`cardAsks`) are `partsListedBesideCard`'s. A part that
 * waits only for the user's file or link, asked on no card, comes last, under
 * a line that asks for it (wave H M11: "I couldn't do this: - Both cuts as
 * mp4 files"). Every line renders whole or throws (`chatT`, `ruleLine`).
 */
export function notDoneLines(
  notDone: QuestionnaireChatNotDone,
  assetPanelHeading: string,
  lng: string | undefined,
  cardAsks = false,
): Array<string> {
  const t = chatT(lng);
  const origin =
    notDone.origin === "objective_panel" ? "objectivePanel" : "chat";
  const parts = partsListedBesideCard(notDone.parts, cardAsks);
  const undone = parts.filter((part) => part.status === "not_done");
  // A part whose only reason is the file or link it waits for from the user
  // (`material`, `link`; L9) is a request for it, never a refusal.
  const waiting = undone.filter(waitsForUser);
  const notDoneAt = (where: "asset_panel" | null) =>
    undone.filter(
      (part) =>
        !waitsForUser(part) &&
        (part.where === "asset_panel") === (where === "asset_panel"),
    );
  const bullet = (part: QuestionnaireNotDonePart): string => {
    if (!part.capability) return `- ${part.what}`;
    // What was done instead follows the design the turn committed under.
    const nearest =
      part.status === "nearest"
        ? capabilityNearestKey(part.capability, notDone.exposure)
        : null;
    return nearest
      ? `- ${part.what}: ${t(`questionnaire.capabilityNearest.${nearest}`)}`
      : `- ${part.what} (${capabilityWords(part.capability, notDone.exposure, t, lng)})`;
  };
  const bullets = (parts: ReadonlyArray<QuestionnaireNotDonePart>) => {
    // Parts a code rule's refusal left undone: the rule's catalog line is the
    // reason, in the chat's language (L4a), told once with every part it names
    // (the grouping the refusal lines use), at the first of them.
    const refused = groupRefusalEntries(
      parts.flatMap((part) =>
        part.refusal
          ? [{ line: ruleLine(part.refusal.code, part.refusal.params, lng), questions: [part.what] }]
          : [],
      ),
    );
    const told = new Set<string>();
    return parts.flatMap((part) => {
      if (!part.refusal) return [bullet(part)];
      const line = ruleLine(part.refusal.code, part.refusal.params, lng);
      if (told.has(line)) return [];
      told.add(line);
      const named = refused.find((entry) => entry.line === line)?.questions ?? [];
      return [`- ${new Intl.ListFormat(lng, { type: "conjunction" }).format(named)}: ${line}`];
    });
  };
  const group = (
    parts: ReadonlyArray<QuestionnaireNotDonePart>,
    line: "nearest" | "panel" | "none" | "waiting",
    panel?: string,
  ) =>
    parts.length > 0
      ? [
          t(`questionnaire.notDone.${origin}.${line}`, {
            count: parts.length,
            panel,
          }),
          ...bullets(parts),
        ]
      : [];
  return [
    ...group(parts.filter((part) => part.status === "nearest"), "nearest"),
    ...group(notDoneAt("asset_panel"), "panel", assetPanelHeading),
    ...group(notDoneAt(null), "none"),
    ...group(waiting, "waiting"),
  ];
}

/**
 * Whether a not-done part waits for the user's file (`material`, L9) and for
 * nothing the builder cannot do: no rule's refusal, no capability, not the asset
 * panel's. Its line asks for it. A link is never one: the chat adds it itself.
 */
function waitsForUser(part: QuestionnaireNotDonePart): boolean {
  return (
    Boolean(part.material) &&
    !part.refusal &&
    !part.capability &&
    part.where !== "asset_panel"
  );
}
